import { FC, HTMLAttributes, useCallback } from 'react';
import cn from 'classnames';

import * as ModalWindows from '@Stores/ModalWindows';
import Link from '@Navigation/Link';
import BackgroundImage from '@UI/BackgroundImage';
import Image from '@UI/Image';
import Button from '@UI/Button';
import DiscountTag from '@UI/DiscountTag';
import useRenderType from '@Hooks/useRenderType';
import Condition from '@Promo/elems/Condition/Condition';
import { PromoCardData } from '@Promo/typings';
import styles from './PromoCard.module.css';

export interface PromoCardProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  card: PromoCardData;
}

const PromoCard: FC<PromoCardProps> = (props) => {
  const { card, ...restProps } = props;
  const { isCSR } = useRenderType();
  const isService = card.type === 'service';

  const handlePromoClick = useCallback(() => {
    if (window.cancelClick) return;

    switch (card.buttonEvent) {
      case 'sampleOrder':
        ModalWindows.open('OrderTextileSamples');
        break;

      case 'videoConsultation':
        ModalWindows.open('VideoConsultation');
        break;

      default:
    }
  }, [card.buttonEvent]);

  return (
    <div
      {...restProps}
      className={styles.wrapper}
      style={card.bgColor ? { backgroundColor: card.bgColor } : undefined}
      onClick={handlePromoClick}
    >
      <Link className={styles.card} to={card.link}>
        {card.icon && <BackgroundImage src={card.icon} className={styles.icon} />}
        {card.bgImage && <Image src={card.bgImage} className={styles.bgImage} />}

        {/**
         * TODO: bug
         * Сейчас рендерим содержимое только на клиенте (в браузере), так как на сервере некоторая часть
         * периодически рендерится неккоретно и причина этого неизвестна
         */}
        {isCSR && (
          <>
            {card.period && !isService && (
              <div
                className={cn(styles.period, {
                  [styles.lightText]: card.theme === 'dark',
                  [styles.grafitText]: card.theme === 'grafit',
                  [styles.darkText]: card.periodColor === 'dark',
                  [styles.whiteText]: card.periodColor === 'light',
                })}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: card.period }} // чтобы можно было вставлять текст с неразрывными пробелами
              />
            )}

            <div
              className={cn(styles.title, {
                [styles.lightText]: card.theme === 'dark',
                [styles.grafitText]: card.theme === 'grafit',
              })}
              dangerouslySetInnerHTML={{ __html: card.title }}
            />

            {card.description && (
              <div
                className={cn(styles.description, {
                  [styles.lightText]: card.theme === 'dark',
                  [styles.grafitText]: card.theme === 'grafit',
                })}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: card.description }} // чтобы можно было вставлять текст с неразрывными пробелами
              />
            )}
            {card.sticker && (
              <DiscountTag
                className={styles.discount}
                size='xl'
                color={card.sticker.color}
                theme={card.sticker.type === 'default' ? 'new' : 'white'}
              >
                {card.sticker.text}
              </DiscountTag>
            )}

            {card.buttonText && (
              <div className={styles.serviceBtnWrapper}>
                <Button theme={card.theme === 'light' ? 'transparent' : 'transparent-light'} wide>
                  {card.buttonText}
                </Button>
              </div>
            )}
            {card.conditionText && (
              <Condition className={styles.condition} text={card.conditionText} />
            )}
          </>
        )}
      </Link>
    </div>
  );
};

export default PromoCard;
